/**
 * useWorkspaceActionHandlers Hook
 * 
 * Handles workspace-level actions:
 * - Delete workspace
 * - Leave workspace
 */

import React, { useEffect } from "react";
import { auth, storage } from "../../../firebase";
import { ref, deleteObject } from "firebase/storage";
import { Workspace } from "../../../types";
import { api } from "../../../services/api";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";
import { DemoModeMessage } from "../../../utils/demoModeMessage";

export interface UseWorkspaceActionHandlersParams {
  workspaceId: string;
  workspace: Workspace | null;
  isOwner: boolean;
  setDeleteDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setDeleting: React.Dispatch<React.SetStateAction<boolean>>;
  setDeleteWorkspaceName: React.Dispatch<React.SetStateAction<string>>;
  allowNavigationRef: React.MutableRefObject<boolean>;
  setLeaveDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setLeaving: React.Dispatch<React.SetStateAction<boolean>>;
  setLeaveWorkspaceName: React.Dispatch<React.SetStateAction<string>>;
  setSnackbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSnackbarElement: React.Dispatch<React.SetStateAction<React.ReactElement | null>>;
  deleting: boolean;
  leaveWorkspaceName: string;
  deleteWorkspaceName: string;
}

export interface UseWorkspaceActionHandlersReturn {
  handleDeleteWorkspace: () => Promise<void>;
  handleLeaveWorkspace: () => Promise<void>;
}

/**
 * Hook for workspace action handlers
 */
export function useWorkspaceActionHandlers({
  workspaceId,
  workspace,
  isOwner,
  setDeleteDialogOpen,
  setDeleting,
  setDeleteWorkspaceName,
  allowNavigationRef,
  setLeaveDialogOpen,
  setLeaving,
  setLeaveWorkspaceName,
  setSnackbarOpen,
  setSnackbarElement,
  deleting,
  leaveWorkspaceName,
  deleteWorkspaceName,
}: UseWorkspaceActionHandlersParams): UseWorkspaceActionHandlersReturn {
  // Prevent tab/window close during workspace deletion (but allow intentional navigation)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): string | void => {
      // Only show warning if deleting AND navigation is not allowed (intentional navigation)
      // Use ref to get the current value synchronously
      if (deleting && !allowNavigationRef.current) {
        // Modern browsers ignore custom messages, but we can still trigger the warning
        e.preventDefault();
        // Chrome requires returnValue to be set
        e.returnValue = "";
        return "";
      }
    };

    if (deleting) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [deleting, allowNavigationRef]);

  const handleDeleteWorkspace = async (): Promise<void> => {
    if (!workspace) return;

    // DEMO MODE: Block in production, allow in development
    // PRODUCTION MODE: Continue with normal delete flow
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: Workspace cannot be deleted. Please log in to delete workspace." />
      );
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
      setDeleting(false);
      allowNavigationRef.current = false;
      return;
    }

    setDeleting(true);
    allowNavigationRef.current = false; // Prevent navigation during deletion
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Delete logo from storage first (before workspace is deleted)
      if (workspace.logoUrl) {
        try {
          const logoRef = ref(storage, workspace.logoUrl);
          await deleteObject(logoRef);
        } catch (error) {
          console.log("Error deleting logo:", error);
        }
      }

      // Delete workspace using backend API
      await api.deleteWorkspace(workspaceId);

      // Allow navigation now - deletion was successful and we're intentionally navigating away
      // Set ref before navigation so beforeunload handler doesn't show warning
      allowNavigationRef.current = true;

      // Backend has already updated activeWorkspaceId if there are other workspaces,
      // or cleared it if no other workspaces exist
      // Fetch remaining workspaces to determine navigation
      try {
        const remainingWorkspaces = await api.getWorkspaces();
        
        if (remainingWorkspaces.length > 0) {
          // User has other workspaces - navigate to first one
          // The backend has already set activeWorkspaceId, so App.tsx will handle navigation
          // Use full page reload to ensure clean state
          window.location.href = "/";
        } else {
          // No other workspaces - navigate to landing/create workspace page
          window.location.href = "/create-workspace";
        }
      } catch (error) {
        // If fetching workspaces fails, fall back to home page
        // App.tsx will handle navigation based on activeWorkspaceId
        console.error("Error fetching workspaces after deletion:", error);
        window.location.href = "/";
      }
    } catch (error) {
      console.error("Error deleting workspace:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to delete workspace. Please try again.";
      alert(errorMessage);
      // On error, reset flags but keep dialog open
      allowNavigationRef.current = false;
      setDeleting(false);
    }
    // Note: We don't reset deleting flag in finally because if navigation is allowed,
    // we're about to navigate away anyway and the component will unmount
  };

  const handleLeaveWorkspace = async (): Promise<void> => {
    if (!workspace) return;

    // DEMO MODE: Block in production, allow in development
    // PRODUCTION MODE: Continue with normal leave flow
    if (isDemoMode() && !isBypassAuthEnabled()) {
      setSnackbarElement(
        <DemoModeMessage message="Demo mode: You cannot leave the workspace in demo mode." />
      );
      setSnackbarOpen(true);
      setLeaveDialogOpen(false);
      setLeaving(false);
      return;
    }

    setLeaving(true);
    try {
      // Leave workspace using backend API
      await api.leaveWorkspace(workspaceId);

      // Backend has already updated activeWorkspaceId if there are other workspaces,
      // or cleared it if no other workspaces exist
      // Redirect to home page - App.tsx will handle navigation based on activeWorkspaceId
      window.location.href = "/";
    } catch (error) {
      console.error("Error leaving workspace:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to leave workspace. Please try again.";
      alert(errorMessage);
    } finally {
      setLeaving(false);
    }
  };

  return {
    handleDeleteWorkspace,
    handleLeaveWorkspace,
  };
}
